'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { NavDropdownItem, NavItem } from '../../types/navigation';

const MORE_ITEM_LABEL = 'Más';
const MIN_VISIBLE_ITEMS = 1;
const NAV_OVERFLOW_SAFETY_SPACE = 24;
const COURSES_NAV_ITEM_HREF = '/cursos';
const COURSES_NAV_ITEM_LABEL = 'Cursos';
const MORE_ITEM_MEASUREMENT_DROPDOWN: NavDropdownItem[] = [{ href: '#', label: 'placeholder' }];

interface OverflowLayout {
  isOverflowing: boolean;
  visibleRegularItemCount: number;
}

interface VisibleRegularItemCountParams {
  availableWidth: number;
  moreWidth: number;
  regularItemWidths: number[];
  reservedPinnedWidth: number;
}

const getElementInnerWidth = (element: HTMLElement) => {
  const styles = window.getComputedStyle(element);
  const paddingLeft = Number.parseFloat(styles.paddingLeft) || 0;
  const paddingRight = Number.parseFloat(styles.paddingRight) || 0;

  return element.clientWidth - paddingLeft - paddingRight - NAV_OVERFLOW_SAFETY_SPACE;
};

const isCoursesNavItem = (item: NavItem) =>
  item.href === COURSES_NAV_ITEM_HREF || item.label === COURSES_NAV_ITEM_LABEL;

const getMeasuredItemWidth = (measurement: HTMLElement, item: NavItem) => {
  const element = measurement.querySelector<HTMLElement>(`[data-nav-measure-item="${item.label}"]`);

  return element?.offsetWidth || 0;
};

const getMeasuredMoreWidth = (measurement: HTMLElement) =>
  measurement.querySelector<HTMLElement>('[data-nav-measure-more]')?.offsetWidth || 0;

const calculateVisibleRegularItemCount = ({
  availableWidth,
  moreWidth,
  regularItemWidths,
  reservedPinnedWidth,
}: VisibleRegularItemCountParams) => {
  const reservedWidth = moreWidth + reservedPinnedWidth;
  const minimumVisibleItems = reservedPinnedWidth > 0 ? 0 : MIN_VISIBLE_ITEMS;
  let usedWidth = 0;
  let visibleRegularItemCount = 0;

  regularItemWidths.some((width) => {
    if (usedWidth + width + reservedWidth > availableWidth) {
      return true;
    }

    usedWidth += width;
    visibleRegularItemCount += 1;

    return false;
  });

  return Math.max(minimumVisibleItems, visibleRegularItemCount);
};

const createMoreItem = (overflowItems: NavItem[]): NavItem | null => {
  if (!overflowItems.length) {
    return null;
  }

  const dropdown: NavDropdownItem[] = overflowItems.map((item) => ({
    badge: item.badge,
    badgeColor: item.badgeColor,
    href: item.href,
    label: item.label,
  }));

  return {
    dropdown,
    href: '#',
    label: MORE_ITEM_LABEL,
  };
};

const useHeaderOverflowMenu = (navItems: NavItem[]) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const measurementRef = useRef<HTMLUListElement>(null);
  const [overflowLayout, setOverflowLayout] = useState<OverflowLayout>({
    isOverflowing: false,
    visibleRegularItemCount: navItems.length,
  });

  const pinnedCoursesItem = useMemo(() => navItems.find(isCoursesNavItem) || null, [navItems]);

  const regularItems = useMemo(
    () => navItems.filter((item) => item !== pinnedCoursesItem),
    [navItems, pinnedCoursesItem],
  );

  const recalculateVisibleItems = useCallback(() => {
    const container = containerRef.current;
    const measurement = measurementRef.current;

    const availableWidth = container ? getElementInnerWidth(container) : 0;

    if (!container || !measurement || availableWidth <= 0) {
      return;
    }

    const navItemWidths = navItems.map((item) => getMeasuredItemWidth(measurement, item));
    const moreWidth = getMeasuredMoreWidth(measurement);
    const totalWidth = navItemWidths.reduce((total, width) => total + width, 0);

    if (totalWidth <= availableWidth) {
      setOverflowLayout({
        isOverflowing: false,
        visibleRegularItemCount: navItems.length,
      });
      return;
    }

    const reservedPinnedWidth = pinnedCoursesItem
      ? getMeasuredItemWidth(measurement, pinnedCoursesItem)
      : 0;
    const regularItemWidths = regularItems.map((item) => getMeasuredItemWidth(measurement, item));
    const visibleRegularItemCount = calculateVisibleRegularItemCount({
      availableWidth,
      moreWidth,
      regularItemWidths,
      reservedPinnedWidth,
    });

    setOverflowLayout({
      isOverflowing: true,
      visibleRegularItemCount,
    });
  }, [navItems, pinnedCoursesItem, regularItems]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(recalculateVisibleItems);

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [recalculateVisibleItems]);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return undefined;
    }

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', recalculateVisibleItems);

      return () => {
        window.removeEventListener('resize', recalculateVisibleItems);
      };
    }

    const observer = new ResizeObserver(recalculateVisibleItems);
    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [recalculateVisibleItems]);

  const visibleItems = useMemo(() => {
    if (!overflowLayout.isOverflowing) {
      return navItems;
    }

    const visibleRegularItems = new Set(
      regularItems.slice(0, overflowLayout.visibleRegularItemCount),
    );

    return navItems.filter((item) => item === pinnedCoursesItem || visibleRegularItems.has(item));
  }, [navItems, overflowLayout, pinnedCoursesItem, regularItems]);

  const overflowItems = useMemo(() => {
    if (!overflowLayout.isOverflowing) {
      return [];
    }

    return regularItems.slice(overflowLayout.visibleRegularItemCount);
  }, [overflowLayout, regularItems]);

  const moreItem = useMemo(() => createMoreItem(overflowItems), [overflowItems]);
  const displayItems = useMemo(() => {
    if (!moreItem) {
      return visibleItems;
    }

    return [...visibleItems, moreItem];
  }, [moreItem, visibleItems]);

  return {
    containerRef,
    displayItems,
    measurementRef,
    moreItem,
  };
};

export { MORE_ITEM_LABEL, MORE_ITEM_MEASUREMENT_DROPDOWN };
export default useHeaderOverflowMenu;
